import React from 'react';
import clsx from 'clsx';
import useCheckoutState from '../../hooks/useCheckoutState';
import useCartCost from '../../hooks/useCartCost';
import { isSinglePwyw, selectablePrices } from '../../lib/multiPrice';
import StyledElement from '../StyledElement';
import PayWhatYouWantPriceField from '../PricePicker/PayWhatYouWantPriceField';

/**
 * Where the single-price PWYW input renders: `variables.elements.checkout.pwyw.position`,
 * one of content-top | content-bottom | form-top | form-bottom (default content-top).
 */
function readPwywPosition(theme) {
  return theme?.elements?.checkout?.pwyw?.position || 'content-top';
}

/**
 * Self-contained single pay-what-you-want amount input. Mounted at four call
 * sites — Product.js (content-top, content-bottom) and CheckoutForm.js
 * (form-top, form-bottom) — each passing its own `position` prop. This is the
 * single decision site for the 4-way position control: it renders nothing
 * unless the product is a single PWYW product (no picker) AND the theme's
 * resolved position matches the calling site's `position` prop, so exactly one
 * site ever mounts the field for a given theme value.
 *
 * `selectablePrices()[0]` is the lone price; a legacy charge-PWYW product has no
 * prices[], so fall back to the product's min amount.
 */
export default function ProductPwywField({ className, position = 'content-top' }) {
  const { checkout, query, theme } = useCheckoutState();
  const { productPriceAmount } = useCartCost();
  const product = checkout.product;

  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type/payWhatYouWant via isSinglePwyw)
  if (!isSinglePwyw(product, query)) return null;
  if (readPwywPosition(theme) !== position) return null;

  const onlyPrice = selectablePrices(product?.prices, query)[0];

  return (
    <StyledElement
      elementPath="checkout.form.pwyw"
      as="div"
      className={clsx('cp_pwyw-wrapper', className)}
    >
      <PayWhatYouWantPriceField
        price={
          // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.currency)
          onlyPrice || {
            amount: productPriceAmount,
            currency: product.currency,
            payWhatYouWant: true,
          }
        }
        // Show the currency code to the right of the input, matching the
        // multi-price picker's PWYW field — the amount alone doesn't make the
        // currency clear.
        showCurrency
      />
    </StyledElement>
  );
}
